import { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, TrendingDown, ShoppingCart, Truck, AlertTriangle,
  Package, RefreshCw, Banknote, Boxes,
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { moneda, cantidad, completarDias } from '../utils/format';
import toast from 'react-hot-toast';

// ── Helpers ────────────────────────────────────────────────────────────────
function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches';
}

function todayLabel() {
  return new Date().toLocaleDateString('es-AR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
}

function trendPct(current, prev) {
  if (!prev || prev === 0) return null;
  return Math.round(((current - prev) / prev) * 100);
}

// ── Trend badge ────────────────────────────────────────────────────────────
function Trend({ value }) {
  if (value === null || value === undefined) return null;
  const up = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full ${
      up
        ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400'
        : 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400'
    }`}>
      {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
      {Math.abs(value)}%
    </span>
  );
}

// ── KPI Card ───────────────────────────────────────────────────────────────
const colorMap = {
  primary: {
    iconBg:    'bg-primary-50 dark:bg-primary-500/10',
    iconColor: 'text-primary-600 dark:text-primary-400',
  },
  green: {
    iconBg:    'bg-emerald-50 dark:bg-emerald-500/10',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
  },
  blue: {
    iconBg:    'bg-sky-50 dark:bg-sky-500/10',
    iconColor: 'text-sky-600 dark:text-sky-400',
  },
  red: {
    iconBg:    'bg-rose-50 dark:bg-rose-500/10',
    iconColor: 'text-rose-600 dark:text-rose-400',
  },
};

function KpiCard({ icon: Icon, label, value, sub, trend, color = 'primary', alert = false }) {
  const c = colorMap[color];
  return (
    <div className={`card dark:bg-slate-800 flex flex-col gap-4 ${alert ? 'ring-1 ring-rose-200 dark:ring-rose-500/30 bg-rose-50/40 dark:bg-rose-500/5' : ''}`}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl ${c.iconBg} flex items-center justify-center shrink-0`}>
          <Icon size={19} className={c.iconColor} />
        </div>
        <Trend value={trend} />
      </div>
      <div>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">{label}</p>
        <p className={`text-[1.75rem] font-bold tabular-nums leading-none ${alert ? 'text-rose-700 dark:text-rose-400' : 'text-slate-900 dark:text-white'}`}>
          {value}
        </p>
        {sub && <p className="text-xs text-slate-400 mt-2">{sub}</p>}
      </div>
    </div>
  );
}

// ── Tooltip del gráfico ────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-dropdown px-4 py-3 text-sm border border-slate-100 dark:border-slate-700">
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-slate-900 dark:text-white font-bold tabular-nums">{moneda(payload[0].value)}</p>
    </div>
  );
}

// ── Empty state del chart ──────────────────────────────────────────────────
function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center h-52 text-slate-200 dark:text-slate-700">
      <Package size={40} strokeWidth={1.5} />
      <p className="text-sm mt-3 text-slate-400">{message}</p>
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user }                  = useAuth();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [lastUpdated, setUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/resumen.php');
      setData(res.data);
      setUpdated(new Date());
    } catch {
      toast.error('No se pudo cargar el dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) return (
    <div className="flex items-center justify-center h-64 text-slate-300 dark:text-slate-600">
      <RefreshCw size={22} className="animate-spin" />
    </div>
  );

  if (!data) return null;

  // Datos procesados
  const graficoDias   = completarDias(data.ventas_7dias ?? []);
  const ventasAyer    = graficoDias[graficoDias.length - 2]?.total ?? 0;
  const trendVentas   = trendPct(data.ventas_hoy?.monto ?? 0, ventasAyer);
  const semanaTotal   = graficoDias.reduce((s, d) => s + d.total, 0);
  const bajosStock    = data.bajo_stock ?? [];
  const topProductos  = data.top_productos ?? [];
  const maxVendido    = Math.max(...topProductos.map(p => p.total_vendido), 1);
  const firstName     = user?.nombre?.split(' ')[0] ?? 'Usuario';

  // Colores de las barras de top productos (tonos del acento)
  const barColors = ['#7175ef', '#8b8ff4', '#a5a8f7', '#bfc1fa', '#d4d5fc'];

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400 capitalize">{todayLabel()}</p>
          <h1 className="text-[1.6rem] font-bold text-slate-900 dark:text-white mt-0.5 leading-tight">
            {greeting()}, {firstName}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-col items-end">
          <button
            onClick={fetchData}
            disabled={loading}
            className="btn-ghost text-sm disabled:opacity-40"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          {lastUpdated && (
            <p className="text-[11px] text-slate-300 dark:text-slate-600">
              Hace {Math.round((Date.now() - lastUpdated) / 60000) || '< 1'} min
            </p>
          )}
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          icon={ShoppingCart}
          label="Ventas hoy"
          value={moneda(data.ventas_hoy?.monto)}
          sub={`${data.ventas_hoy?.cantidad} operación/es`}
          trend={trendVentas}
          color="primary"
        />
        <KpiCard
          icon={TrendingUp}
          label="Ganancia estimada"
          value={moneda(data.ganancia_hoy)}
          sub="Basada en costos actuales"
          color="green"
        />
        <KpiCard
          icon={Truck}
          label="Compras hoy"
          value={moneda(data.compras_hoy?.monto)}
          sub={`${data.compras_hoy?.cantidad} compra/s`}
          color="blue"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Bajo stock"
          value={data.stock?.bajo_stock}
          sub={`de ${data.stock?.total_productos} productos`}
          color="red"
          alert={data.stock?.bajo_stock > 0}
        />
      </div>

      {/* ── Gráficos ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-3 gap-5">

        {/* Area chart ventas */}
        <div className="card dark:bg-slate-800 lg:col-span-3 xl:col-span-2">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-white">Ventas — últimos 7 días</h2>
              <p className="text-[11px] text-slate-400 mt-1">
                Total semana:{' '}
                <span className="font-semibold text-slate-600 dark:text-slate-300">{moneda(semanaTotal)}</span>
              </p>
            </div>
          </div>

          {graficoDias.every(d => d.total === 0) ? (
            <EmptyState message="Sin ventas esta semana" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={graficoDias} margin={{ left: -10, right: 4 }}>
                <defs>
                  <linearGradient id="gradVentas" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#7175ef" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#7175ef" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis
                  dataKey="dia"
                  tick={{ fontSize: 11, fill: '#94A3B8', fontFamily: 'Plus Jakarta Sans' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#94A3B8', fontFamily: 'Plus Jakarta Sans' }}
                  axisLine={false}
                  tickLine={false}
                  width={65}
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  content={<ChartTooltip />}
                  cursor={{ stroke: '#7175ef', strokeWidth: 1.5, strokeDasharray: '4 4' }}
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#7175ef"
                  strokeWidth={2.5}
                  fill="url(#gradVentas)"
                  dot={false}
                  activeDot={{ r: 5, fill: '#7175ef', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top productos */}
        <div className="card dark:bg-slate-800 lg:col-span-2 xl:col-span-1">
          <div className="mb-5">
            <h2 className="text-sm font-bold text-slate-900 dark:text-white">Top productos</h2>
            <p className="text-[11px] text-slate-400 mt-1">Más vendidos — últimos 30 días</p>
          </div>

          {topProductos.length === 0 ? (
            <EmptyState message="Sin datos de ventas" />
          ) : (
            <div className="space-y-4">
              {topProductos.slice(0, 5).map((p, i) => (
                <div key={p.nombre}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-bold text-slate-300 dark:text-slate-600 w-3.5 shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-[13px] font-medium text-slate-700 dark:text-slate-300 truncate">
                        {p.nombre}
                      </span>
                    </div>
                    <span className="text-[12px] font-semibold text-slate-500 dark:text-slate-400 tabular-nums shrink-0 ml-2">
                      {cantidad(p.total_vendido)}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(p.total_vendido / maxVendido * 100).toFixed(1)}%`,
                        backgroundColor: barColors[i] ?? '#c7c9fb',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Bajo stock ── */}
      {bajosStock.length > 0 && (
        <div className="card dark:bg-slate-800">
          <div className="flex items-center gap-2 mb-5">
            <AlertTriangle size={15} className="text-rose-500 shrink-0" />
            <h2 className="text-sm font-bold text-slate-900 dark:text-white">Alerta de stock</h2>
            <span className="ml-auto badge-low">{bajosStock.length} producto{bajosStock.length > 1 ? 's' : ''}</span>
          </div>
          <div className="space-y-3.5">
            {bajosStock.map((p) => {
              const pct      = p.stock_minimo > 0
                ? Math.min(100, (Number(p.stock_actual) / Number(p.stock_minimo)) * 100)
                : 0;
              const sinStock = Number(p.stock_actual) === 0;
              const barColor = sinStock ? 'bg-red-500' : pct < 50 ? 'bg-orange-400' : 'bg-yellow-400';
              return (
                <div key={p.id}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[13px] font-medium text-slate-700 dark:text-slate-300 truncate">
                        {p.nombre}
                      </span>
                      {p.categoria && (
                        <span className="badge-neutral shrink-0">{p.categoria}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      <span className={`text-[12px] font-bold tabular-nums ${sinStock ? 'text-red-600 dark:text-red-400' : 'text-orange-500 dark:text-orange-400'}`}>
                        {cantidad(p.stock_actual, p.unidad)}
                      </span>
                      <span className="text-[11px] text-slate-300 dark:text-slate-600">
                        / {cantidad(p.stock_minimo, p.unidad)}
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${barColor} transition-all`}
                      style={{ width: sinStock ? '2%' : `${Math.max(pct, 3)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Valor del inventario ── */}
      <div className="card-sm dark:bg-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center">
            <Boxes size={15} className="text-primary-500 dark:text-primary-400" />
          </div>
          <span className="text-[12px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            Valor total del inventario
          </span>
        </div>
        <span className="font-bold text-slate-900 dark:text-white text-xl tabular-nums">
          {moneda(data.stock?.valor_inventario)}
        </span>
      </div>

    </div>
  );
}
